// src/components/stages/TriageFieldsEditor.tsx
// The "Triage" section's own four fields (risk level, pain scale, allergies,
// current medications) — shared by PreConsultStage.tsx's own Section A and
// ConsultStage.tsx's doctor-facing correction form (PreConsultAmendForm), so
// the two always render identically instead of drifting the way a
// hand-copied form does: ConsultStage's form originally re-implemented these
// controls with a plain number input for pain scale and no risk-level colour
// coding, which looked and behaved differently from what the nurse actually
// used to record it.
import VoiceMicButton from '../VoiceMicButton';
import { SegmentedControl } from '../ui/SegmentedControl';
import { RISK_LEVEL_TONE } from '../ui/RiskBadge';

export const RISK_LEVELS = ['Low', 'Medium', 'High', 'Critical'] as const;
export type RiskLevel = typeof RISK_LEVELS[number];

// 0-10 as a segmented control (per the Pre-Consult wireframe) rather than a
// bare number input — a pain score is a point on a fixed 11-point scale, not
// an open-ended quantity, and a row of taps reads faster than typing a digit.
export const PAIN_SCALE_OPTIONS = Array.from({ length: 11 }, (_, i) => String(i));

export interface TriageFieldsValue {
  risk_level: string;
  pain_scale: string;
  allergies: string;
  current_meds: string;
}

export type TriageFieldKey = keyof TriageFieldsValue;
export const ALL_TRIAGE_FIELD_KEYS: TriageFieldKey[] = ['risk_level', 'pain_scale', 'allergies', 'current_meds'];

export function TriageFieldsEditor({ value, onChange, enabledFields, disabled }: {
  value: TriageFieldsValue;
  onChange: (key: keyof TriageFieldsValue, v: string) => void;
  /** Which of the four fields this ailment template actually wants — e.g. a
   * Diabetes Assessment has no reason to ask a pain scale. `null`/`undefined`
   * (no template selected, or a template that's never touched this setting)
   * means all four, exactly the fixed set this component always showed
   * before templates could configure it. */
  enabledFields?: TriageFieldKey[] | null;
  /** Read-only rendering (StageEditModal's default view before "Edit" is
   * clicked) — SegmentedControl needs this explicitly so its selected option
   * stays fully legible rather than fading with the rest of a disabled
   * `<fieldset>`; the plain inputs below are covered by that fieldset alone. */
  disabled?: boolean;
}) {
  const enabled = new Set(enabledFields ?? ALL_TRIAGE_FIELD_KEYS);
  return (
    <>
      {enabled.has('risk_level') && (
        <div className="mb-3">
          <label className="lbl">Risk level</label>
          <SegmentedControl
            options={RISK_LEVELS.map(r => ({ value: r, label: r, tone: RISK_LEVEL_TONE[r] as 'success' | 'warn' | 'caution' | 'danger' | 'info' }))}
            value={value.risk_level}
            onChange={v => onChange('risk_level', v)}
            disabled={disabled}
          />
        </div>
      )}
      {enabled.has('pain_scale') && (
        <div className="mb-3">
          <label className="lbl">Pain scale (0-10)</label>
          <SegmentedControl
            size="sm"
            options={PAIN_SCALE_OPTIONS.map(n => ({ value: n, label: n }))}
            value={value.pain_scale}
            onChange={v => onChange('pain_scale', v)}
            disabled={disabled}
          />
        </div>
      )}
      {enabled.has('allergies') && (
        <div className="mb-3">
          <label className="lbl" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span>Known allergies</span>
            <VoiceMicButton onText={t => onChange('allergies', (value.allergies ? value.allergies + ' ' : '') + t)} />
          </label>
          <input value={value.allergies} onChange={e => onChange('allergies', e.target.value)} placeholder="NKDA or list…" />
        </div>
      )}
      {enabled.has('current_meds') && (
        <div>
          <label className="lbl" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span>Current medications</span>
            <VoiceMicButton onText={t => onChange('current_meds', (value.current_meds ? value.current_meds + ' ' : '') + t)} />
          </label>
          <input value={value.current_meds} onChange={e => onChange('current_meds', e.target.value)} placeholder="List ongoing medications…" />
        </div>
      )}
    </>
  );
}
